import './confirmation.css';

import EventEmitter from "eventemitter3";
import { Modal } from '../modal';
import { Util } from '../../../utils/util';
import { EventTypes } from '../../../core/event-bus';
import { Modals, OrderType, Pages, PaymentPaymentType } from '../../../types/enums';
import { OrderPriceSummary } from '../../shared/OrderPriceSummary/order-price-summary';
import { Order } from '../../../models/order';
import { User } from '../../../models/user';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { Data } from '../../../models/data';
import { MenuItemCartCardController } from '../../shared/MenuItemCartCard/menu-item-cart-card-controller';
import { DataLayerService } from '../../../services/data-layer-service';
import { ProfileService } from '../../../services/profile-service';
import { InOrder } from '../../../online-ordering/interfaces/online-ordering.interface';
import { IOrderPayment } from '../../../models/order.interface';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { IConfirmationModules } from './confirmation.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { IDataStoreInfo } from '../../../models/data.interface';

/**
 * The Confirmation class represents a modal dialog displayed to users upon confirming an order.
 * It includes features like rendering sections for "Thank You", order details, and user actions.
 * 
 * @extends Modal
 */
export class Confirmation extends Modal {
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _user: User;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _profileService: ProfileService;
    private readonly _modules: IConfirmationModules;
    private _storeInfo: IDataStoreInfo = {} as IDataStoreInfo;
    private _orderId: number = -1;

    /**
    * Creates an instance of the Confirmation modal.
    * @param {EventEmitter} eventBus - The event bus for emitting and listening to events.
    * @param {Data} data - The data store containing application-wide configurations.
    * @param {Order} order - The current order object.
    * @param {User} user - The current user object.
    * @param {DataLayerService} dataLayer - The current data layer service.
    * @param {ILoyaltyProvider} loyaltyProvider - The loyalty provider for handling rewards and discounts.
    */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, user: User, dataLayer: DataLayerService, loyaltyProvider: ILoyaltyProvider, profileService: ProfileService) {
        super(
            "modal_confirmation",
            eventBus,
            context,
            dataLayer,
            localizationService,
            {
                hasCloseButton: true,
                onClose: () => eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING),
                header: { title: "ORDER CONFIRMATION" },
                ariaLabel: "Order Confirmation"
            }
        );
        this._data = data;
        this._order = order;
        this._user = user;
        this._loyaltyProvider = loyaltyProvider;
        this._profileService = profileService;
        this._modules = this._initializeModules();
        this._eventBusListeners();
    }

    private _initializeModules(): IConfirmationModules {
        return {
            menuItems: new MenuItemCartCardController({
                parentDivId: "div_confirmation_order_summary",
                eventBus: this._eventBus,
                data: this._data,
                context: this._context,
                localizationService: this._localizationService,
                order: this._order,
                dataLayerService: this._dataLayer,
                loyaltyProvider: this._loyaltyProvider,
                profileService: this._profileService
            }),
            summary: new OrderPriceSummary(this._eventBus, "div_confirmation_totals", this._data, this._context, this._localizationService, this._order, this._loyaltyProvider, this._profileService, { isConfirmation: true }),
        };
    }

    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.ORDER_COMPLETED, this._orderPlaced);
    }

    private readonly _orderPlaced = (nOrder: InOrder): void => {
        this._orderId = nOrder.OrderID;
        this._renderAllSections(nOrder);
        this._setEventListeners();

        if (Util.isAppView() && typeof orderCompleted == "function")
            orderCompleted();
    }

    /**
     * Renders all sections of the confirmation modal.
     * This includes the "Thank You" section, order details, summary, and buttons.
     * @private
     */
    private readonly _renderAllSections = (nOrder: InOrder): void => {
        this._storeInfo = this._data.getProperty("StoreInfo");
        this._renderThankYouSection(nOrder);
        this._renderOrderItemsSection();
        this._renderOrderSummary(nOrder);
        this._renderSummarySection(nOrder);
        this._renderButtons();

        this._order.startNewOrder(true);
    }

    /**
     * Sets up event listeners for user interaction with the modal buttons.
     * @protected
     */
    protected readonly _setEventListeners = (): void => {
        Util.setElement("onclick", "btn_confirmation_create_profile", this._createProfileOnClick);
        Util.setElement("onclick", "btn_confirmation_track_order", this._trackOrderOnClick);
        Util.setElement("onclick", "btn_confirmation_back_home", this._backToHomeOnClick);
        Util.setElement("onclick", "btn_confirmation_map_directions", this._mapGetDirectionsOnClick);
    }

    /**
     * @private
     */
    private readonly _mapGetDirectionsOnClick = (): void => {
        let addressString = this._formatStoreAddress();
        if (addressString) {
            if (addressString.includes("<br>"))
                addressString = addressString.replaceAll(/<br\s*\/?>/gi, ' ');
            this._openInNativeMapApp(addressString);
        } else {
            console.warn("Element #addressString not found.");
        }
    }

    /**
     * Handles the "Create Profile" button click event.
     * Navigates to the signup modal.
     * @private
     */
    private readonly _createProfileOnClick = (): void => {
        this.close();
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP);
    }

    /**
     * Handles the "Track Order" button click event.
     * Navigates to the order tracking page.
     * @private
     */
    private readonly _trackOrderOnClick = (): void => {
        this.close();
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.TRACK_ORDER, { orderId: this._orderId, fromRecentOrdersPage: false });

        if (this._dataLayer)
            this._dataLayer.track_order_click(this._orderId, this._data.getProperty("StoreInfo").storeCode.toString());
    }

    /**
     * Handles the "Back to Home" button click event.
     * Navigates back to the landing page.
     * @private
     */
    private readonly _backToHomeOnClick = (): void => {
        this.close();

        const homepageRedirectUrl = this._data.getBrandProperty("Settings").OOS.homepageRedirectUrl;
        if (Util.isAppView() || !homepageRedirectUrl) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
            return;
        }

        Util.redirect(homepageRedirectUrl);
    }

    /**
     * Renders the "Thank You" section of the modal.
     * Dynamically replaces placeholders with user information.
     * @private
     */
    private readonly _renderThankYouSection = (nOrder: InOrder): void => {
        const userName = nOrder.Customer?.Name || "";
        Util.setElement("innerText", "h3_confirmation_thank_you_name", this._localizationService.names("ThankYouFirstName").replace("{{first_name}}", userName));

        // Determine confirmation message key
        const hasNotifications = this._data.getProperty("Notification").hasNotifications;
        const isDelivery = this._order.getProperty("OrderTypeID") === OrderType.DELIVERY;
        let confirmationMessageKey = "YourOrderWillBeReady";

        if (hasNotifications && isDelivery)
            confirmationMessageKey = "YouCanTrackYourOrderWhichWillBeDelivered";
        else if (hasNotifications)
            confirmationMessageKey = "YouCanTrackYourOrderWhichWillBeReady";
        else if (isDelivery)
            confirmationMessageKey = "YourOrderWillBeDelivered";

        // Set user's email and confirmation message
        const userEmail = nOrder.Customer?.Email || "";

        let orderTime = this._order.getProperty("WaitTime").toString();
        let messageKeySuffix = "InAboutMinutes";
        let timeReplacementString = "{{minutes}}";
        const futureDate = this._order.getProperty("FutureDate");

        if (futureDate) {
            orderTime = this._getOrderTime(nOrder);
            messageKeySuffix = "At";
            timeReplacementString = "{{dateAndTime}}";
        }
        confirmationMessageKey += messageKeySuffix;
        const confirmationMessage = this._localizationService.names(confirmationMessageKey).replace(timeReplacementString, orderTime);

        Util.setElement("innerHTML", "p_confirmation_thank_you_email", `${this._localizationService.names("YoullReceiveEmailConfirmation").replace("{{email}}", userEmail)}<br>${confirmationMessage}`);
    }

    /**
     * Renders the order items section.
     * @private
     */
    private readonly _renderOrderItemsSection = (): void => {
        if (this._modules.menuItems.ui)
            this._modules.menuItems.ui.init(false, true);
    }

    /**
     * Renders the order summary section, including totals and pricing breakdown.
     * @private
     */
    private readonly _renderOrderSummary = (nOrder?: InOrder): void => {
        this._modules.summary.init(nOrder);
    }

    /**
     * Renders the summary section of the confirmation modal with order details and store information.
     * @private
     */
    private readonly _renderSummarySection = (nOrder: InOrder): void => {
        const sectionDiv = document.getElementById("div_confirmation_order_details");
        if (!sectionDiv)
            return;

        sectionDiv.innerHTML = "";

        sectionDiv.appendChild(this._createSummarySectionGroup(this._localizationService.names("OrderNumber"), nOrder.OrderID.toString()));
        sectionDiv.appendChild(this._createPickupOrDeliveryTimeSectionGroup(nOrder));

        const orderPlacedGroup = this._createOrderPlacedSectionGroup(nOrder);
        if (orderPlacedGroup)
            sectionDiv.appendChild(orderPlacedGroup);

        const paymentMethodGroup = this._createPaymentMethodSectionGroup(nOrder);
        if (paymentMethodGroup)
            sectionDiv.appendChild(paymentMethodGroup);

        if (this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY)
            sectionDiv.appendChild(this._createStoreAddressSectionGroup());

        sectionDiv.appendChild(this._createStorePhoneSummarySectionGroup());
    }

    /**
     * Builds the "Estimated Delivery Time" or "Ready for Pickup/Curbside By" section group.
     * @private
     */
    private readonly _createPickupOrDeliveryTimeSectionGroup = (nOrder: InOrder): HTMLElement => {
        const timeFullString = this._getOrderTime(nOrder);

        if (this._order.getProperty("OrderTypeID") === OrderType.DELIVERY)
            return this._createSummarySectionGroup(this._localizationService.names("EstimatedDeliveryTime"), timeFullString);

        const name = nOrder.Contactless
            ? this._localizationService.names("READY_FOR_CURBSIDE_PICKUP_BY")
            : this._localizationService.names("READY_FOR_PICKUP_BY");
        return this._createSummarySectionGroup(name, timeFullString);
    }

    /**
     * Builds the "Order Placed" section group, or null when the order has no end time yet.
     * @private
     */
    private readonly _createOrderPlacedSectionGroup = (nOrder: InOrder): HTMLElement | null => {
        const orderEndTime = nOrder.OrderEndTime;
        if (!orderEndTime)
            return null;

        const country = this._data.getProperty("Countries").find(x => x.CountryID === this._data.getProperty("StoreInfo").countryId);
        const dateTimeString = Util.formatDateTimeFull(orderEndTime, country?.IsoCode || null);
        return this._createSummarySectionGroup(this._localizationService.names("OrderPlaced"), dateTimeString);
    }

    /**
     * Builds the "Payment Method" section group, or null when there's nothing to show.
     * @private
     */
    private readonly _createPaymentMethodSectionGroup = (nOrder: InOrder): HTMLElement | null => {
        if (!nOrder.Payments || nOrder.Payments.length === 0)
            return null;

        let payments = "";
        for (const payment of nOrder.Payments)
            payments += this._formatPaymentMethodLine(payment);

        if (payments === "")
            return null;

        return this._createSummarySectionGroup(this._localizationService.names("PaymentMethod"), payments.slice(0, -4));
    }

    /**
     * Formats a single payment as an HTML line (including trailing <br>) for the payment method section.
     * @private
     */
    private readonly _formatPaymentMethodLine = (payment: IOrderPayment): string => {
        switch (payment.PaymentTypeID) {
            case PaymentPaymentType.CREDIT_CARD_WALLET:
                return `${this._localizationService.names("PaidWithSavedCreditCard")}<br>`;
            case PaymentPaymentType.CASH:
                return `${this._localizationService.names("ToBePaidInCash")}<br>`;
            case PaymentPaymentType.CREDIT_CARD: {
                const { last4, cardName } = Util.ResolveCreditCardDisplay(payment);
                if (!last4) return "";
                const text = this._localizationService.names("CreditCardEndingWith").replace("{{card_type}}", cardName).replace("{{last_four}}", last4);
                return `${text}<br>`;
            }
            case PaymentPaymentType.CREDIT_CARD_ON_DELIVERY:
                return `${this._localizationService.names("ToBePaidByCreditCardUponDelivery")}<br>`;
            case PaymentPaymentType.GIFT_CARD: {
                const text = this._localizationService.names("GiftCardEndingWith").replace("{{last_four}}", payment.AccountID.slice(-4));
                return `${text}<br>`;
            }
            default:
                return "";
        }
    }

    private readonly _getOrderTime = (nOrder: InOrder): string => {
        const country = this._data.getProperty("Countries").find(x => x.CountryID === this._data.getProperty("StoreInfo").countryId);
        let promiseTime = nOrder.PromiseTime;
        const waitTime = this._order.getProperty("WaitTime");
        const futureDate = this._order.getProperty("FutureDate");
        let timeFullString = "";

        if (futureDate) {
            const iDate = new Date(futureDate);
            timeFullString = Util.formatDateTime(Util.DateAdd(iDate, this._order.getProperty("FutureMinute"), "m"));
        } else if (this._data.getProperty("Settings").ISCC) {// && globalThis.aOLO.Temp.SelectedStoreID > 0) {
            const store = Util.getCallCenterStore(this._order.getProperty("StoreID"), this._data.getProperty("Stores"));
            if (store) {
                const timeOffset = this._data.getProperty("TimeOffset");
                const storeWaitTime = OnlineOrderingUtil.getStoreWaitTime(this._data, store, this._order.getProperty("OrderTypeID"));
                timeFullString = Util.formatDateTime(Util.DateAdd(Util.NowStore(timeOffset), storeWaitTime, "m"));
            }
        } else {
            const promiseTimeString = this._formatTimeByLocale(promiseTime, country?.IsoCode || null);
            timeFullString = this._localizationService.names("ConfirmationPromiseTime").replace("{{time}}", promiseTimeString).replace("{{minute_low}}", `${waitTime}`).replace("{{minute_high}}", `${waitTime + 5}`);
        }

        return timeFullString;
    }

    /**
     * @param {Date | string} date - The date to be formatted. Can be a `Date` object or an ISO string.
     * @param {string | null} [language=null] - The language to use for formatting (e.g., "en-US", "es-MX").
     * @returns {string} - The formatted date string (e.g., "6:00 PM").
     */
    private _formatTimeByLocale(date: Date | string, language: string | null = null): string {
        if (language == null)
            language = Util.GetBrowserLanguage();

        const options = {
            hour: "numeric",
            minute: "2-digit"
        } as const;

        const newDate = new Date(date);
        const formatter = new Intl.DateTimeFormat(language, options);
        return formatter.format(newDate);
    }

    /**
     * Creates a summary section group with a header and body.
     * @private
     * @param {string} header - The header text for the section.
     * @param {string} body - The body content for the section.
     * @returns {HTMLElement} - The created HTML element for the summary section.
     */
    private readonly _createSummarySectionGroup = (header: string, body: string): HTMLElement => {
        const html = `
            <div class="confirmation-details-group">
                <h4>${header}</h4>
                <p>${body}</p>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Creates a section group for the store's phone number with a call-to-action link.
     * @private
     * @returns {HTMLElement} - The created HTML element for the phone section.
     */
    private readonly _createStorePhoneSummarySectionGroup = (): HTMLElement => {
        const storePhoneNumber = this._data.getProperty("StoreInfo").phone;
        const formattedPhoneNumber = Util.formatPhoneNumberByCountryId(storePhoneNumber, this._data.getProperty("StoreInfo").countryId, this._data.getProperty("Countries"));

        const html = `
            <div class="confirmation-group-phone">
                <div class="confirmation-details-group">
                    <h4>${this._localizationService.names("StorePhone")}</h4>
                    <p>${formattedPhoneNumber}</p>
                </div>
                <div>
                    <a id="a_confirmation_phone" href="#" class="button-link">${this._localizationService.names("Call")}</a>
                </div>
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);

        const phoneElement = element.querySelector("#a_confirmation_phone") as HTMLAnchorElement | null;
        if (phoneElement)
            phoneElement.onclick = (e: Event) => {
                e.preventDefault();

                if (Util.isAppView() && typeof openPhone == "function")
                    openPhone(storePhoneNumber);
                else
                    window.location.assign(`tel:${storePhoneNumber}`);
            };

        return element;
    }

    /**
     * 
     * @returns
     */
    private readonly _createStoreAddressSectionGroup = (): HTMLElement => {
        const addressString = this._formatStoreAddress();
        const html = `
            <div class="confirmation-group-phone">
                <div class="confirmation-details-group">
                    <h4>${this._localizationService.names("StoreAddress")}</h4>
                    <p>${addressString}</p>
                </div>
                <div>
                    <button id="btn_confirmation_map_directions" class="button-link">
                         ${this._localizationService.names("GetDirections")}
                    </button>
                </div>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }
    /**
     * Formats the store address based on the store information and available countries data.
     * @private
     * @returns {string} - The formatted store address as an HTML string.
     */
    private readonly _formatStoreAddress = (): string => {
        const storeInfo = this._storeInfo;
        const address = Util.formatAddressObject(
            {
                StreetNo: storeInfo.streetNumber,
                Address1: storeInfo.address1,
                Address2: storeInfo.address2,
                Address3: storeInfo.address3,
                Address4: storeInfo.address4,
                Address5: storeInfo.address5,
                City: storeInfo.city,
                State: storeInfo.state,
                ZipCode: storeInfo.zip,
                CountryID: storeInfo.countryId || 1,
                AddressTypeID: 0,
            },
            this._data.getProperty("Countries") || []
        );

        return `${address.address1}${address.address2 ? `<br>${address.address2}` : ""}<br>${address.cityState}`;
    }
    /**
     * Renders buttons for the confirmation modal and adjusts their visibility based on the user's login status.
     * @private
     */
    private _renderButtons(): void {
        Util.showElement("btn_confirmation_back_home");
        Util.hideElement("btn_confirmation_create_profile");
        Util.hideElement("btn_confirmation_track_order");

        const isOrderHub = this._context.getProperty("isOrderHub");
        if (isOrderHub)
            Util.hideElement("btn_confirmation_back_home");

        if (!this._user.isLoggedIn() && !isOrderHub)
            Util.showElement("btn_confirmation_create_profile");

        const hasNotify = this._data.getProperty("Notification").hasNotifications;
        if (hasNotify)
            Util.showElement("btn_confirmation_track_order");
    }

    /**
     * 
     * @param address
     * @returns
     */
    private _openInNativeMapApp(address: string): void {
        const lat = this._storeInfo.latitude;
        const lng = this._storeInfo.longitude;

        if (Util.isAppView()) {
            if (lat != null && lng != null) {
                (window as any).openDirectionsToLocation(
                    JSON.stringify({ latitude: lat, longitude: lng })
                );
            }
            return;
        }

        if (!address) {
            console.warn("No address provided.");
            return;
        }

        const encodedAddress = encodeURIComponent(address.trim());
        const isAppleDevice = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
        const browserUrl = isAppleDevice
            ? `https://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`
            : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`;

        window.open(browserUrl, "_blank");
    }
}
